import React, { useState, useCallback, memo } from 'react';
import type { ExtendedWord } from '../../types';
import { getWordImageUrl, getWordImageFallbackUrl, PLACEHOLDER_IMAGE } from '../../utils/imageUtils';
import { getDisplayWord } from '../../utils/wordUtils';

interface WordImageProps {
  word: ExtendedWord;
  alt?: string;
  language?: 'he' | 'en';
  className?: string;
  containerClassName?: string;
  showRing?: boolean;
  ringColor?: string;
}

/**
 * Shared component for displaying word images with proper error handling.
 * Handles fallback chain: primary image -> fallback image -> placeholder.
 */
export const WordImage: React.FC<WordImageProps> = memo(({
  word,
  alt,
  language = 'he',
  className = 'w-full h-full object-contain',
  containerClassName = 'w-64 h-64 bg-white dark:bg-dark-surface rounded-3xl shadow-lg p-6 flex items-center justify-center',
  showRing = false,
  ringColor = 'ring-purple-300 dark:ring-purple-600',
}) => {
  const [errorCount, setErrorCount] = useState(0);

  const handleError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.currentTarget;
    
    if (errorCount === 0) {
      // First error: try fallback URL
      setErrorCount(1);
      target.src = getWordImageFallbackUrl(word);
    } else {
      // Second error: use placeholder
      setErrorCount(2);
      target.src = PLACEHOLDER_IMAGE;
    }
  }, [errorCount, word]);

  // Reset error count when word changes
  React.useEffect(() => {
    setErrorCount(0);
  }, [word.id]);

  const computedAlt = alt || getDisplayWord(word, language);
  const ringClass = showRing ? `ring-4 ${ringColor} ring-opacity-50` : '';

  return (
    <div className={`${containerClassName} ${ringClass}`}>
      <img 
        src={getWordImageUrl(word)}
        alt={computedAlt}
        className={className}
        onError={handleError}
      />
    </div>
  );
});

WordImage.displayName = 'WordImage';
